import {
  assertInInjectionContext,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  Signal,
  signal,
  untracked,
} from '@angular/core';
import { GuardService } from '../services/guard.service';

export type GuardType = 'SHELL/LOGOUT' | string;

/**
 * A `GuardFn` that returns **`true`** means the user is allowed to do/execute something
 * When a `GuardFn` returns **`false`** the execution is stopped
 * This means the concept of a ``guard`` is similar to the concept of guards in Angular
 * Guards are **always run in ``untracked`` by default
 */
export type GuardFn = (() => boolean) | (() => Promise<boolean>);

export interface GuardParams {
  active: boolean | (() => boolean) | Signal<boolean>;
  guard: GuardFn;
  injector?: Injector;
}

export interface GuardRef {
  type: GuardType;
  unregister: () => void;
  execute: GuardFn;
}

export function registerGuard(type: GuardType, params: NonNullable<GuardParams>) {
  assertInInjectionContext(registerGuard);

  const service = inject(GuardService);
  const destroyRef = inject(DestroyRef);

  const guardRef = signal<GuardRef | undefined>(undefined);

  let ref: GuardRef | undefined;

  const active = computed(
    typeof params.active === 'boolean' ? () => params.active : params.active
  );

  effect(
    (onCleanup) => {
      if (active()) {
        ref = service.registerGuard(type, () => untracked(() => params.guard()) as any);
        guardRef.set(ref);
      } else {
        if (ref) {
          service.unregisterGuard(ref);
        }

        guardRef.set(undefined);
      }

      onCleanup(() => {
        ref?.unregister?.();
        guardRef.set(undefined);
      });
    },
    { injector: params.injector }
  );

  destroyRef.onDestroy(() => {
    ref?.unregister();
    guardRef.set(undefined);
  });

  return guardRef.asReadonly();
}
